import { ActionContext, ActionTree } from "vuex";
import { StatusCodes } from "http-status-codes";
import {
  ICoreErrorResponse,
  ICoreResponse,
} from "@/core/interfaces/coreResponse.interface";
import ApprovalRequestService from "../services/approvalRequest.service";
import { IApprovalRequestsState } from "../types";
import { StoreState } from "@/store/type";
import { AUTH_GETTERS } from "@/modules/Auth/vuex/getters";
import type { User } from "@/modules/Auth/types";
import notificationService from "@/core/services/notification.service";
import { NOTIFICATION_TYPES } from "@/core/enums/NOTIFICATION_TYPES";
import { NOTIFICATION_MESSAGES } from "@/core/constants/notificationMessages";
import analyticsService from "@/analytic.service";
import {
  APPROVAL_DELEGATION_ANALYTICS_EVENTS,
  APPROVAL_REQUEST_ANALYTICS_EVENTS,
} from "../constants/analyticsEvents";
import { FetchApprovalRequestsParams } from "../models/fetchApprovalRequestsParams.request";
import {
  ApprovalRequestActionPayload,
  ApprovalRequestCancelPayload,
  ApprovalRequestOverridePayload,
} from "../models/approvalRequestAction.request";
import { CreateOrUpdateApprovalDelegationDto } from "../models/approvalDelegation.request";
import {
  unwrapApprovalRequestDetail,
  unwrapApprovalRequestList,
} from "../utils/normalizeApprovalRequest";

const approvalRequestService = new ApprovalRequestService();

const actions: ActionTree<IApprovalRequestsState, StoreState> = {
  async fetchApprovalRequests(
    context: ActionContext<IApprovalRequestsState, StoreState>,
    params: FetchApprovalRequestsParams = {},
  ) {
    try {
      context.commit("setFetchingApprovalRequests", true);
      const response: ICoreResponse =
        await approvalRequestService.listApprovalRequests(params);

      if (response.success && response.status === StatusCodes.OK) {
        const { items, total } = unwrapApprovalRequestList(response.data);
        context.commit("setApprovalRequests", items);
        context.commit("setTotalRecords", total);
        if (params.page) context.commit("setPage", params.page);
        if (params.page_size) context.commit("setPageSize", params.page_size);
        analyticsService.logActionEvent(
          APPROVAL_REQUEST_ANALYTICS_EVENTS.APPROVAL_REQUEST_LIST_FETCH,
          params,
        );
      } else {
        throw response;
      }
    } catch (error: unknown) {
      context.commit("setApprovalRequests", []);
      context.commit("setTotalRecords", 0);
      const exception = error as ICoreErrorResponse;
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    } finally {
      context.commit("setFetchingApprovalRequests", false);
    }
  },

  async fetchApprovalRequestDetail(
    context: ActionContext<IApprovalRequestsState, StoreState>,
    payload: {
      requestId: string;
      successHandler?: () => void;
      errorHandler?: () => void;
    },
  ) {
    try {
      context.commit("setFetchingApprovalRequestDetail", true);
      const response: ICoreResponse =
        await approvalRequestService.getApprovalRequestDetail(
          payload.requestId,
        );

      if (response.success && response.status === StatusCodes.OK) {
        const detail = unwrapApprovalRequestDetail(response.data);
        context.commit("setSelectedApprovalRequest", detail);
        analyticsService.logActionEvent(
          APPROVAL_REQUEST_ANALYTICS_EVENTS.APPROVAL_REQUEST_DETAIL_FETCH,
          { requestId: payload.requestId },
        );
        payload.successHandler?.();
      } else {
        throw response;
      }
    } catch (error: unknown) {
      context.commit("setSelectedApprovalRequest", null);
      payload.errorHandler?.();
      const exception = error as ICoreErrorResponse;
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    } finally {
      context.commit("setFetchingApprovalRequestDetail", false);
    }
  },

  async actionApprovalRequest(
    context: ActionContext<IApprovalRequestsState, StoreState>,
    {
      requestId,
      body,
      successHandler,
      errorHandler,
    }: {
      requestId: string;
      body: ApprovalRequestActionPayload;
      successHandler?: () => void;
      errorHandler?: (error: ICoreErrorResponse) => void;
    },
  ) {
    try {
      context.commit("setApprovalRequestActionLoading", true);
      const response: ICoreResponse =
        await approvalRequestService.actionApprovalRequest(requestId, body);

      if (response.success && response.status === StatusCodes.OK) {
        const detail = unwrapApprovalRequestDetail(response.data);
        if (detail) {
          context.commit("setSelectedApprovalRequest", detail);
        }
        analyticsService.logActionEvent(
          APPROVAL_REQUEST_ANALYTICS_EVENTS.APPROVAL_REQUEST_ACTION,
          { requestId, actions: body.actions },
        );
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          response.message ||
          NOTIFICATION_MESSAGES.APPROVAL_REQUEST_ACTION_SUCCESS;
        successHandler?.();
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;
      errorHandler?.(exception);
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
    } finally {
      context.commit("setApprovalRequestActionLoading", false);
      notificationService.triggerNotification();
    }
  },

  async overrideApprovalRequest(
    context: ActionContext<IApprovalRequestsState, StoreState>,
    {
      requestId,
      body,
      successHandler,
      errorHandler,
    }: {
      requestId: string;
      body: ApprovalRequestOverridePayload;
      successHandler?: () => void;
      errorHandler?: (error: ICoreErrorResponse) => void;
    },
  ) {
    try {
      context.commit("setApprovalRequestActionLoading", true);
      const response: ICoreResponse =
        await approvalRequestService.overrideApprovalRequest(requestId, body);

      if (response.success && response.status === StatusCodes.OK) {
        const detail = unwrapApprovalRequestDetail(response.data);
        if (detail) {
          context.commit("setSelectedApprovalRequest", detail);
        }
        analyticsService.logActionEvent(
          APPROVAL_REQUEST_ANALYTICS_EVENTS.APPROVAL_REQUEST_OVERRIDE,
          { requestId, status: body.status },
        );
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          response.message ||
          NOTIFICATION_MESSAGES.APPROVAL_REQUEST_OVERRIDE_SUCCESS;
        successHandler?.();
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;
      errorHandler?.(exception);
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
    } finally {
      context.commit("setApprovalRequestActionLoading", false);
      notificationService.triggerNotification();
    }
  },

  async cancelApprovalRequest(
    context: ActionContext<IApprovalRequestsState, StoreState>,
    {
      requestId,
      body,
      successHandler,
      errorHandler,
    }: {
      requestId: string;
      body: ApprovalRequestCancelPayload;
      successHandler?: () => void;
      errorHandler?: (error: ICoreErrorResponse) => void;
    },
  ) {
    try {
      context.commit("setApprovalRequestActionLoading", true);
      const response: ICoreResponse =
        await approvalRequestService.cancelApprovalRequest(requestId, body);

      if (response.success && response.status === StatusCodes.OK) {
        const detail = unwrapApprovalRequestDetail(response.data);
        if (detail) {
          context.commit("setSelectedApprovalRequest", detail);
        }
        analyticsService.logActionEvent(
          APPROVAL_REQUEST_ANALYTICS_EVENTS.APPROVAL_REQUEST_CANCEL,
          { requestId },
        );
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          response.message ||
          NOTIFICATION_MESSAGES.APPROVAL_REQUEST_CANCEL_SUCCESS;
        successHandler?.();
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;
      errorHandler?.(exception);
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
    } finally {
      context.commit("setApprovalRequestActionLoading", false);
      notificationService.triggerNotification();
    }
  },

  async fetchApprovalDelegations(
    context: ActionContext<IApprovalRequestsState, StoreState>,
  ) {
    const user = context.rootGetters[AUTH_GETTERS.USER] as User | null;
    const delegatorId = user?.organization_user_data?.id ?? "";
    if (!delegatorId) return;

    try {
      context.commit("setFetchingApprovalDelegations", true);
      const response: ICoreResponse =
        await approvalRequestService.getApprovalDelegations(delegatorId);
      if (response.success && response.status === StatusCodes.OK) {
        const delegations = Array.isArray(response.data)
          ? response.data
          : response.data?.data || [];
        context.commit("setApprovalDelegations", delegations);
        analyticsService.logActionEvent(
          APPROVAL_DELEGATION_ANALYTICS_EVENTS.LIST_FETCH,
          { count: delegations.length },
        );
      } else {
        throw response;
      }
    } catch {
      context.commit("setApprovalDelegations", []);
    } finally {
      context.commit("setFetchingApprovalDelegations", false);
    }
  },

  async fetchAssignedApprovalDelegations(
    context: ActionContext<IApprovalRequestsState, StoreState>,
  ) {
    const user = context.rootGetters[AUTH_GETTERS.USER] as User | null;
    const delegateId = user?.organization_user_data?.id ?? "";
    if (!delegateId) return;

    try {
      context.commit("setFetchingAssignedApprovalDelegations", true);
      const response: ICoreResponse =
        await approvalRequestService.getAssignedApprovalDelegations(delegateId);
      if (response.success && response.status === StatusCodes.OK) {
        const delegations = Array.isArray(response.data)
          ? response.data
          : response.data?.data || [];
        context.commit("setAssignedApprovalDelegations", delegations);
        analyticsService.logActionEvent(
          APPROVAL_DELEGATION_ANALYTICS_EVENTS.ASSIGNED_LIST_FETCH,
          { count: delegations.length },
        );
      } else {
        throw response;
      }
    } catch {
      context.commit("setAssignedApprovalDelegations", []);
    } finally {
      context.commit("setFetchingAssignedApprovalDelegations", false);
    }
  },

  async createOrUpdateApprovalDelegation(
    context: ActionContext<IApprovalRequestsState, StoreState>,
    {
      delegatorId,
      body,
      successHandler,
      errorHandler,
    }: {
      delegatorId: string;
      body: CreateOrUpdateApprovalDelegationDto;
      successHandler: () => void;
      errorHandler?: (error: ICoreErrorResponse) => void;
    },
  ) {
    const isUpdate = Boolean(body.public_id);
    try {
      context.commit("setApprovalDelegationActionLoading", true);
      const response: ICoreResponse =
        await approvalRequestService.createOrUpdateApprovalDelegation(
          delegatorId,
          body,
        );
      if (
        response.success &&
        (response.status === StatusCodes.OK ||
          response.status === StatusCodes.CREATED)
      ) {
        successHandler();
        await context.dispatch("fetchApprovalDelegations");
        analyticsService.logActionEvent(
          isUpdate
            ? APPROVAL_DELEGATION_ANALYTICS_EVENTS.UPDATE
            : APPROVAL_DELEGATION_ANALYTICS_EVENTS.CREATE,
          {
            publicId: body.public_id ?? "",
            delegateId: body.delegate_id,
          },
        );
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          response.message ||
          (isUpdate
            ? NOTIFICATION_MESSAGES.APPROVAL_DELEGATION_UPDATE_SUCCESS
            : NOTIFICATION_MESSAGES.APPROVAL_DELEGATION_CREATE_SUCCESS);
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;
      errorHandler?.(exception);
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
    } finally {
      context.commit("setApprovalDelegationActionLoading", false);
      notificationService.triggerNotification();
    }
  },

  async deleteApprovalDelegation(
    context: ActionContext<IApprovalRequestsState, StoreState>,
    {
      publicId,
      successHandler,
    }: {
      publicId: string;
      successHandler?: () => void;
    },
  ) {
    const user = context.rootGetters[AUTH_GETTERS.USER] as User | null;
    const delegatorId = user?.organization_user_data?.id ?? "";
    if (!delegatorId) return;

    try {
      context.commit("setApprovalDelegationActionLoading", true);
      const response: ICoreResponse =
        await approvalRequestService.deleteApprovalDelegation(
          delegatorId,
          publicId,
        );
      if (response.success && response.status === StatusCodes.OK) {
        const updatedDelegations = context.state.approvalDelegations.filter(
          (delegation) => delegation.public_id !== publicId,
        );
        context.commit("setApprovalDelegations", updatedDelegations);
        successHandler?.();
        analyticsService.logActionEvent(
          APPROVAL_DELEGATION_ANALYTICS_EVENTS.DELETE,
          { publicId },
        );
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          response.message ||
          NOTIFICATION_MESSAGES.APPROVAL_DELEGATION_DELETE_SUCCESS;
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
    } finally {
      context.commit("setApprovalDelegationActionLoading", false);
      notificationService.triggerNotification();
    }
  },
};

export default actions;
